import { defineManifest } from '@ndrc/app-sdk/manifest';
import fr from './messages/fr.json' with { type: 'json' };

/**
 * CRM — le fichier client de l'entreprise simulée, fiche par fiche.
 *
 * Séance 2 de la progression décidée par l'enseignant : le Tableur d'abord (la grille brute), le
 * CRM ensuite. La même donnée, deux regards.
 *
 * Cette app **n'interprète rien** : ni score, ni température de prospect, ni segment, ni catégorie
 * toute faite (correction pédagogique du 7 septembre 2026). Elle montre les champs qui viennent du
 * fichier distribué, les colonnes que l'élève a créées lui-même dans le Tableur, et — depuis le
 * suivi commercial du 7 septembre 2026 — **ce que l'élève écrit lui-même** : ses notes, ses
 * échanges, le statut qu'il pose. Décider ce qui rend un prospect prioritaire reste l'exercice de
 * l'élève, pas le décor de l'outil.
 *
 * Elle ne possède aucune table : tout ce qu'elle affiche et tout ce qu'elle écrit vit dans les
 * entités communes du cœur (`core.contacts`, `core.accounts`, `core.interactions`), par `ctx.data`,
 * sous la RLS de l'espace de l'élève. Les trois permissions d'écriture (`contacts.write`,
 * `interactions.write`) portent EXACTEMENT trois gestes : poser le statut, écrire des notes,
 * enregistrer un échange — jamais une correction de coordonnées, qui reste le métier du Tableur.
 * Les notes gardent leur propre horodatage dans `attributes.crm` — le champ ouvert d'ADR-008 est
 * nommé par l'app qui l'écrit, jamais partagé, et `attributes.custom` (le Tableur) n'y est jamais
 * touché : chaque écriture relit la ligne et fusionne, sous le verrou optimiste du `DataClient`.
 * `migrations.dir` reste déclaré (le type l'exige) mais le dossier est vide, et le restera tant que
 * le CRM n'aura pas de données propres.
 *
 * **Son identité visuelle est à elle**, et c'est ce manifeste qui la porte — le cœur ne nomme
 * jamais une app (règle 1 de `CLAUDE.md`, §52 du cahier des charges). `color` vient de la palette
 * verrouillée (`docs/design/desktop-reference.md`, `--oran-i`) : une app ne choisit jamais une
 * couleur hors de ce fichier. L'orange est un choix, et il se défend deux fois — c'est la couleur
 * qu'on associe aux CRM du marché, et c'est la seule teinte de la palette qui ne puisse jamais être
 * confondue avec le vert du Tableur dans le dock, où les deux tuiles se touchent. Elle valait
 * `--teal-i` jusqu'au 7 septembre 2026 : personne ne l'avait jamais vue (elle sortait grise), et
 * côte à côte avec le vert du Tableur elle ne disait rien.
 *
 * `glyph` est sa marque, inventée pour cette app le même jour : une **fiche contact** — la carte,
 * la silhouette, les deux lignes de coordonnées — dessinée en blanc sur cet orange. Elle dit ce que
 * fait l'outil sans écrire son nom, comme le logo d'un vrai logiciel. Le `icon: 'users'` reste déclaré comme repli si le glyphe venait à disparaître.
 */
export const manifest = defineManifest({
  id: 'crm',
  name: 'CRM',
  description:
    "Le fichier client de l'entreprise, fiche par fiche : coordonnées, entreprise, origine.",
  icon: 'users',
  color: '#C2410C',
  glyph: [
    // La carte.
    { d: 'M5.6 5.4h12.8a2 2 0 0 1 2 2v9.2a2 2 0 0 1-2 2H5.6a2 2 0 0 1-2-2V7.4a2 2 0 0 1 2-2z' },
    // La silhouette : tête et épaules, pleines.
    { d: 'M9.1 8.75a1.85 1.85 0 1 1 0 3.7 1.85 1.85 0 0 1 0-3.7z', fill: true },
    { d: 'M6.1 15.9c.35-1.85 1.5-2.8 3-2.8s2.65.95 3 2.8z', fill: true },
    // Les deux lignes de coordonnées.
    { d: 'M14.2 10.2h3.9' },
    { d: 'M14.2 13.4h2.8', opacity: 0.7 },
  ],
  version: '0.1.3',
  sdk: '0.0.0',
  status: 'dev',
  category: 'prospection',
  permissions: {
    uses: [
      'contacts.read',
      'contacts.write',
      'accounts.read',
      'interactions.read',
      'tasks.read',
      'tasks.write',
      'interactions.write',
    ],
    defines: [],
  },
  entities: {
    uses: [
      // `lifecycle` : le statut que l'élève POSE lui-même sur la fiche. Deux valeurs, celles que le
      // cœur connaît déjà (`packages/core-datasets/src/ndrc-contacts-preset.ts`, `LIFECYCLE_VALUES`)
      // — pas une de plus : la revue du 7 septembre 2026 a écrit noir sur blanc qu'ajouter un
      // synonyme (« lead », « ancien client ») sans que l'enseignant l'ait nommé, c'est deviner son
      // vocabulaire. Il en nommera un troisième quand il en voudra un.
      { entity: 'contacts', extends: { lifecycle: ['prospect', 'client'] } },
      { entity: 'accounts' },
      { entity: 'tasks' },
      // Les six canaux du cahier des charges (§7 « Interactions ») plus `event`, le salon, que
      // l'enseignant a nommé dans sa demande du 7 septembre 2026 et que les six ne couvrent pas.
      {
        entity: 'interactions',
        extends: {
          channel: ['call', 'email', 'meeting', 'event', 'chat', 'social', 'request'],
        },
      },
    ],
    owns: [],
  },
  events: {
    emits: [],
    subscribes: [],
  },
  ui: {
    student: {
      defaultRoute: '/',
      routes: [{ path: '/', title: 'Contacts' }],
      layout: 'full',
    },
  },
  missions: { actions: [], conditions: ['crm.contactsAtLeast'], blocks: ['crm.inspectContact'] },
  contributes: {
    templateVars: [
      {
        key: 'crm.contactCount',
        label: fr.missionVariables.contactCount,
        resolver: 'missionContactCount',
      },
    ],
  },
  migrations: { schema: 'app_crm', dir: 'migrations' },
});
